import { observer } from "mobx-react-lite";
import * as React from "react";
import { ArrowLeftCircleIcon, ArrowRightCircleIcon } from "@heroicons/react/20/solid";
import { Button } from "@material-tailwind/react";
import "swiper/css/bundle";
import { Swiper, SwiperClass, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/solid";
import DOMPurify from 'dompurify';
import { Virtual } from 'swiper/modules';

import { Issue } from "../Issue";
import { StockistIssue } from "../../../../Common/DTOs/StockistIssue";
import { UpcomingImageStack, UpcomingIssueCard } from "./TextualIssueOutlet";
import { loginSignupController } from "../../../../Common/Components/Standalone/LoginOrSignup/LoginSignupController";
import { alertController } from "../../../../Common/Controllers/AlertController";
import { userAccountController } from "../../../../Common/Controllers/UserAccountController";
import { Dialog } from "../Dialog";
import { issueDetailsController } from "../../Controllers/IssueDetailsController";
import { TitleDetails } from "./AllIssuesOutlet";
import { MAX_ISSUES_AVAILABLE } from "../../../../Common/DTOs/Stock";
import { storageController } from "../../../../Common/Controllers/StoreControllerFactory";

export const IssueDetailsPage = observer(() => {
  const issue = issueDetailsController.issue;
  const [orderDialogOpen, setOrderDialogOpen] = React.useState(false);
  const isIssueLoading = issueDetailsController.isIssueLoading;

  if (issueDetailsController.isMagazineCurrentIssueError) return (
    <div className="w-full h-full mt-6 animate-fadeIn flex flex-col items-center justify-center">
      <span className="text-4xl">Magazine not found</span>
      <Link
        className="mt-4 transition duration-150 ease-in-out text-xl text-main bg-white py-3 px-6 border border-gray-300 rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main"
        to="/latest"
      >
        Home <ArrowRightIcon className="inline w-5 h-5" />
      </Link>
    </div>
  );

  if (!issue && !isIssueLoading) return (
    <div className="w-full h-full mt-6 animate-fadeIn flex flex-col items-center justify-center">
      <span className="text-4xl">Issue not found</span>
      <Link
        className="mt-4 transition duration-150 ease-in-out text-xl text-main bg-white py-3 px-6 border border-gray-300 rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-main"
        to="/latest"
      >
        Home <ArrowRightIcon className="inline w-5 h-5" />
      </Link>
    </div>
  );

  return (
    <>
      <div className="w-full">
        {isIssueLoading ? (
          <div className="animate-fadeIn">
            <MainIssueSkeleton />
          </div>
        ) : issue ? (
          <IssueContent issue={issue} setOrderDialogOpen={setOrderDialogOpen} />
        ) : null}
        <AllIssuesSlider issue={issue} />
      </div>
      {issue && (
        <Dialog
          issue={issue}
          orderDialogOpen={orderDialogOpen}
          setOrderDialogOpen={setOrderDialogOpen}
        />
      )}
    </>
  );
});

const IssueContent = observer(({ issue, setOrderDialogOpen }: { issue: StockistIssue; setOrderDialogOpen: (open: boolean) => void }) => {
  const onSaleMonthYear = new Date(issue.magazineCurrentIssueInfo!.issueOnSale).toLocaleString("en-GB", {
    month: "long",
    year: "numeric",
  });

  const typeLabel = new Date(issue.issueOnSale) > new Date()
    ? "Upcoming Issue"
    : issue.id === issue.magazineCurrentIssueInfo?.id
      ? "Current Issue"
      : "Back Issue";

  const onClickBuyNow = () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in to buy magazines.",
        "warning",
        4000
      );
      loginSignupController.setLoginSignupWindowVisible(true);
      return;
    }
    setOrderDialogOpen(true);
  };

  const store = storageController.getOrCreateStore(issue);
  const canInc = store.orderedAmount < MAX_ISSUES_AVAILABLE;
  const canDec = store.orderedAmount > 0;

  const onInc = () =>
    store.onCopiesChange(Math.min(MAX_ISSUES_AVAILABLE, store.orderedAmount + 1));
  const onDec = () =>
    store.onCopiesChange(Math.max(0, store.orderedAmount - 1));

  const onUpcomingAddToBasketButtonClick = () => {
    if (!userAccountController.isLoggedIn) {
      alertController.displayAlert(
        "Login required",
        "You have to be logged in to add magazines to your basket.",
        "warning",
        4000
      );
      loginSignupController.setLoginSignupWindowVisible(true);
      return;
    }
    store.confirmOrder(issue);
  };

  return (
    <div className="w-full border-b-2 border-gray-300 shadow-[0_5px_6px_0_rgba(200,200,200,0.16)]">
      <div className="overflow-hidden min-h-[476px] grid grid-cols-1 md:grid-cols-[230px_1fr_200px] gap-12 md:gap-8 px-2 xsm:px-4 pb-8 md:pb-14 md:pt-10 max-w-7xl mx-auto md:justify-items-center">
        <div className="align-self-center justify-self-center flex flex-col items-center w-[230px] h-[320px] animate-fadeIn">
          {issue.issueOnSale > new Date() ?
            <UpcomingImageStack issue={issue} linkEnabled={false} daysUntil={Math.ceil((new Date(issue.issueOnSale).getTime() - Date.now()) / (1000 * 60 * 60 * 24))} /> :
            <img
              src={issue.imageURL_230x320}
              alt={`${issue.identity} cover`}
              title={`${issue.magazine.titleDisplay}`}
              className="w-full object-cover rounded-lg shadow-md"
            />
          }
          <div className="text-xs text-gray-500 mt-2">
            {onSaleMonthYear} | {issue.onSaleDateDisplay}
          </div>
        </div>

        <div className="md:ml-12 w-full max-h-[520px] md:max-h-[380px] flex flex-col justify-between overflow-y-hidden animate-fadeIn">
          <div className="flex flex-col justify-center xsm:justify-start items-center xsm:items-start mb-2">
            <div className="font-medium text-center xsm:text-left">
              <Link
                className="transition duration-150 ease-in text-white hover:text-gray-400 cursor-pointer"
                title={`See latest issues of ${issue.magazine.titleDisplay}`}
                to={issue.magazine.stockistMagazineUrl}
                onClick={() => issueDetailsController.setMagazineHint(issue.magazineId)}
              >
                {issue.magazine.titleDisplay}
              </Link>
            </div>
            <div className="flex items-end gap-2 md:gap-3 mt-2">
              <h1 className="text-2xl md:text-4xl tracking-default font-semibold">{issue.identity}</h1>
              <div className={`uppercase text-sm md:text-base tracking-default ${typeLabel === "Back Issue" ? "text-gray-500" : typeLabel === "Upcoming Issue" ? "text-purple" : "text-green-200"}`}>{typeLabel}</div>
            </div>
            <div className="text-xs text-gray-500 mt-4">Release{issue.issueOnSale > new Date() ? "" : "d"}: {issue.onSaleDateDisplay}</div>
          </div>

          <div className="flex-auto flex flex-col gap-2 justify-center xsm:justify-start overflow-y-hidden">
            {issue.stockistIssueText &&
              <div className="min-h-[24px] text-gray-700 overflow-y-auto scrollbar-thin">{issue.stockistIssueText}</div>
            }
            <div className="min-h-[72px] flex-auto flex flex-col overflow-y-hidden">
              <div className="font-medium text-center xsm:text-left">About</div>
              <div className="flex-auto h-fit overflow-y-auto scrollbar-thin">
                <div
                  className="text-gray-500 text-center xsm:text-left"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(issue.magazine.briefDescription)
                  }}
                ></div>
              </div>
            </div>
          </div>

          <div className="flex-none flex flex-col xsm:flex-row items-center gap-4 mt-4">
            <div className="text-xl md:text-2xl font-semibold">RRP{issue.priceDisplay}</div>
            {issue.issueOnSale > new Date() ?
              <div className="justify-center sm:justify-between border-gray-700 flex gap-1">
                <Button
                  className={"rounded-none text-sm !px-4 !py-2 rounded-full whitespace-nowrap"}
                  color={"white"}
                  size="sm"
                  disabled={store.orderedAmount <= 0}
                  onClick={onUpcomingAddToBasketButtonClick}
                >
                  Add to Basket
                </Button>
                <div className="flex items-center">
                  <Button
                    variant="outlined"
                    color="red"
                    onClick={onDec}
                    disabled={!canDec}
                    className="h-10 !py-0 !px-3 md:!px-3 !border-light-grey/50 !text-white text-md hover:!bg-red-500 hover:!border-red-500 transition rounded-r-none"
                    aria-label="decrease"
                  >
                    -
                  </Button>

                  <input
                    type="number"
                    name="quantity"
                    min={0}
                    max={
                      issue.issueOnSale > new Date()
                        ? MAX_ISSUES_AVAILABLE
                        : issue.stock.issuesAvailableIncludingBasket
                    }
                    value={store.orderedAmount}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value || "0", 10) || 0;
                      store.onCopiesChange(
                        Math.max(0, Math.min(parsed, MAX_ISSUES_AVAILABLE - issue.stock.inBasket))
                      );
                    }}
                    className="w-8 md:w-10 h-10 text-center bg-gray-700 text-white border border-gray-600 focus:outline-none !appearance-none rounded-none"
                  />

                  <Button
                    variant="outlined"
                    color="green"
                    onClick={onInc}
                    disabled={!canInc}
                    className="h-10 !py-0 !px-3 md:!px-3 !border-light-grey/50 !text-white text-md hover:!bg-green-500 hover:!border-green-500 transition rounded-l-none"
                    aria-label="increase"
                  >
                    +
                  </Button>
                </div>
              </div> :
              <div className="flex justify-center xsm:justify-start md:justify-between items-center gap-2 w-full">
                <Button
                  className={"text-sm !px-4 xsm:!px-6 !py-3 rounded-full whitespace-nowrap"}
                  disabled={issue.stock.issuesAvailableIncludingBasket <= 0}
                  color={"white"}
                  onClick={onClickBuyNow}
                >Buy Now</Button>
                <span className="whitespace-nowrap text-gray-500">
                  ({issue.stock.issuesAvailableIncludingBasket > 30 ? "30+" : issue.stock.issuesAvailableIncludingBasket} in stock)
                </span>
              </div>
            }
          </div>
        </div>

        <div className="md:border-l md:pl-6 border-gray-700 animate-fadeIn">
          <div className="font-medium mb-3">Further details</div>
          <div className="space-y-1 text-sm text-gray-700">
            <div>Current Issue: {issue.magazineCurrentIssueInfo?.identity}</div>
            <div>Released {issue.magazineCurrentIssueInfo?.onSaleDateDisplay}</div>
            <div>{issue.magazine.frequency} issues per year</div>
          </div>
          {issue.keywords &&
            <>
              <div className="font-medium mt-6 mb-1">Features</div>
              <div className="text-sm text-gray-700">{issue.keywords}</div>
            </>
          }
        </div>
      </div>
    </div>
  );
});

const AllIssuesSlider = observer(({ issue }: { issue: StockistIssue | null }) => {
  const magazine = issue?.magazine;
  const swiperRef = React.useRef<SwiperClass | null>(null);
  const prevClass = `swiper-prev-${issue?.id ?? 'loading'}`;
  const nextClass = `swiper-next-${issue?.id ?? 'loading'}`;
  const {
    isAllIssuesLoading,
    upcoming,
    current,
    back,
    allIssuesOrdered,
  } = issueDetailsController;

  React.useEffect(() => {
    if (!issue) return;
    const swiper = swiperRef.current;
    if (!swiper || !allIssuesOrdered.length) return;

    const index = allIssuesOrdered.findIndex(i => i.id === issue.id);
    if (index === -1) return;
    if (!issueDetailsController.magazineChanged) return;

    const firstVisible = swiper.activeIndex;
    const visibleCount = Math.floor(swiper.params.slidesPerView as number);
    const lastVisible = firstVisible + visibleCount - 1;
    const isVisible = index >= firstVisible && index <= lastVisible;

    if (!isVisible) {
      swiper.slideTo(index);
    }
  }, [issue?.id, allIssuesOrdered.length, issueDetailsController.magazineChanged]);

  if (isAllIssuesLoading) {
    return (
      <div className="mt-10 px-4">
        {magazine ? (
          <h2 className="text-xl font-semibold mb-4">All issues of {magazine.title}</h2>
        ) : (
          <div className="w-48 h-[28px] bg-gray-300/20 rounded animate-pulse mb-4" />
        )}
        <div className="w-full pb-8">
          <div className="flex gap-1.5 overflow-hidden pt-3 pb-2 px-1">
            {Array.from({ length: 10 }).map((_, i) => (
              <div className="animate-fadeIn" key={i}>
                <IssueSliderSkeleton />
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-4 mt-4">
            <div className="w-14 h-14 rounded-full bg-gray-300/20 animate-pulse" />
            <div className="w-14 h-14 rounded-full bg-gray-300/20 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!allIssuesOrdered || allIssuesOrdered.length === 0) {
    return null;
  }

  return (
    <div className="mt-10 px-4">
      <h2 className="text-xl font-semibold mb-4">All issues of {magazine?.title}</h2>
      <div className="w-full pb-8">
        <Swiper
          modules={[Navigation, Virtual]}
          virtual={{
            enabled: true,
            addSlidesBefore: 2,
            addSlidesAfter: 2,
          }}
          navigation={{ prevEl: `.${prevClass}`, nextEl: `.${nextClass}` }}
          spaceBetween={6}
          slidesPerView={1}
          centeredSlides={true}
          onSwiper={(swiper) => swiperRef.current = swiper}
          breakpoints={{
            325: { slidesPerView: 1.25, centeredSlides: false },
            390: { slidesPerView: 1.5, centeredSlides: false },
            510: { slidesPerView: 2, centeredSlides: false },
            620: { slidesPerView: 2.5, centeredSlides: false },
            740: { slidesPerView: 3, centeredSlides: false },
            860: { slidesPerView: 3.5, centeredSlides: false },
            972: { slidesPerView: 4, centeredSlides: false },
            1210: { slidesPerView: 5, centeredSlides: false },
            1440: { slidesPerView: 6, centeredSlides: false },
            1680: { slidesPerView: 7, centeredSlides: false },
            1894: { slidesPerView: 8, centeredSlides: false },
            2128: { slidesPerView: 9, centeredSlides: false },
            2362: { slidesPerView: 10, centeredSlides: false },
            2596: { slidesPerView: 11, centeredSlides: false },
            2830: { slidesPerView: 12, centeredSlides: false },
            3064: { slidesPerView: 13, centeredSlides: false },
          }}
          maxBackfaceHiddenSlides={2}
          className="!pt-3 !pb-2 !px-1"
        >
          {upcoming.map((iss, index) => (
            <SwiperSlide key={iss.id} virtualIndex={index}>
              <UpcomingIssueCard issue={iss} isHighlighted={iss.id === issue?.id} labelText="Coming soon" labelTextColorClass="text-purple" />
            </SwiperSlide>
          ))}
          {current.map((iss, index) => (
            <SwiperSlide key={iss.id} virtualIndex={upcoming.length + index}>
              <Issue onlyThumbnail={false} issue={iss} TitleDetails={TitleDetails} isHighlighted={iss.id === issue?.id} labelText={"Current"} labelTextColorClass="text-green-400" />
            </SwiperSlide>
          ))}
          {back.map((iss, index) => (
            <SwiperSlide key={iss.id} virtualIndex={upcoming.length + current.length + index}>
              <Issue onlyThumbnail={false} issue={iss} TitleDetails={TitleDetails} isHighlighted={iss.id === issue?.id} labelText={"Back issue"} labelTextColorClass="text-gray-800" />
            </SwiperSlide>
          ))}
        </Swiper>

        <div className="flex justify-center gap-4 mt-4">
          <Button className={`p-0 rounded-full ${prevClass}`}>
            <ArrowLeftCircleIcon className="w-14 h-14 hover:bg-gray-300 transition" />
          </Button>
          <Button className={`p-0 rounded-full ${nextClass}`}>
            <ArrowRightCircleIcon className="w-14 h-14 hover:bg-gray-300 transition" />
          </Button>
        </div>
      </div>
    </div>
  );
});

const MainIssueSkeleton = () => {
  return (
    <div className="w-full border-b-2 border-gray-300 shadow-[0_5px_6px_0_rgba(200,200,200,0.16)] animate-pulse">
      <div className="min-h-[476px] grid grid-cols-1 md:grid-cols-[230px_1fr_200px] gap-12 md:gap-8 px-2 xsm:px-4 pb-8 md:pb-14 md:pt-10 max-w-7xl mx-auto md:justify-items-center">

        <div className="flex flex-col items-center align-self-center justify-self-center w-[230px] h-[320px] animate-pulse">
          <div className="w-full h-full bg-gray-300/40 rounded-lg" />
          <div className="w-32 h-4 bg-gray-300/40 rounded mt-3" />
        </div>

        <div className="md:ml-12 w-full flex flex-col gap-4 animate-pulse">
          <div className="w-32 h-5 bg-gray-300/40 rounded" />
          <div className="flex items-end gap-4 mt-2">
            <div className="w-48 h-7 bg-gray-300/40 rounded" />
            <div className="w-16 h-5 bg-gray-300/40 rounded" />
          </div>

          <div className="w-40 h-4 bg-gray-300/40 rounded mt-4" />

          <div className="space-y-2 mt-2">
            <div className="w-full h-3 bg-gray-300/40 rounded" />
            <div className="w-4/5 h-3 bg-gray-300/40 rounded" />
            <div className="w-2/5 h-3 bg-gray-300/40 rounded" />
          </div>

          <div className="mt-6">
            <div className="w-20 h-4 bg-gray-300/40 rounded mb-3" />
            <div className="space-y-2">
              <div className="w-full h-3 bg-gray-300/40 rounded" />
              <div className="w-3/5 h-3 bg-gray-300/40 rounded" />
            </div>
          </div>

          <div className="flex flex-col xsm:flex-row gap-4 mt-6">
            <div className="w-24 h-6 bg-gray-300/40 rounded" />
            <div className="w-32 h-10 bg-gray-300/40 rounded-full" />
          </div>
        </div>

        <div className="md:border-l md:pl-6 border-gray-300/60 space-y-3 animate-pulse">
          <div className="w-24 h-4 bg-gray-300/40 rounded" />

          <div className="space-y-2">
            <div className="w-36 h-3 bg-gray-300/40 rounded" />
            <div className="w-40 h-3 bg-gray-300/40 rounded" />
            <div className="w-24 h-3 bg-gray-300/40 rounded" />
          </div>

          <div className="w-24 h-4 bg-gray-300/40 rounded mt-6" />
          <div className="space-y-2">
            <div className="w-44 h-3 bg-gray-300/40 rounded" />
            <div className="w-32 h-3 bg-gray-300/40 rounded" />
          </div>
        </div>

      </div>
    </div>
  );
};

const IssueSliderSkeleton = () => (
  <div className="flex-shrink-0 flex flex-col items-center justify-between w-[230px] h-[489px] animate-pulse">
    <div className="w-full h-[90%] flex flex-col items-start gap-0.5">
      <div className="w-[230px] h-[320px] bg-gray-300/20 mx-auto" />
      <div className="flex flex-col mt-4 gap-2 w-full items-start justify-between flex-auto">
        <div className="flex flex-col items-start gap-1 w-full">
          <div className="w-3/4 h-5 bg-gray-300/20 rounded" />
          <div className="w-full h-4 bg-gray-300/20 rounded" />
        </div>
        <div className="w-1/2 h-5 bg-gray-300/20 rounded mb-1" />
      </div>
    </div>
    <div className="w-full flex flex-row gap-1 pt-2 border-t border-gray-800">
      <div className="h-[50px] flex-1 bg-gray-300/20 rounded-full" />
      <div className="h-[50px] flex-1 bg-gray-300/20 rounded-full" />
    </div>
  </div>
);
